import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { rolesSelector } from '@selectors/role';
import { Select } from '@components/SharedComponents/DesignSystem/Inputs';
import { RoleEntityInterface } from '@types';
import { capitalize, FormGroup, FormLabel } from '@material-ui/core';
import { currentUserSelector } from '@selectors/user';

import './index.scss';

interface Props {
  id: string;
  rowRoles: RoleEntityInterface[];
  unitRoles?: RoleEntityInterface[];
  label: string | ReactNode | Element;
  onCheckPermission: (id: string, roleId: string, isUserRole: boolean) => void;
  canUpdateRole: boolean;
}

const USER_ROLE = 'user';

const staticRoles = [USER_ROLE];

const PermissionsSelect = ({
  id,
  onCheckPermission,
  rowRoles,
  unitRoles,
  label,
  canUpdateRole,
}: Props) => {
  const roles = useSelector(rolesSelector);
  const currentUser = useSelector(currentUserSelector);

  const renderOptions = () => {
    const basicRoleOptions = roles.map(({ id, name }) => ({
      value: id,
      label: capitalize(name),
      disabled: id === (currentUser?.roles[0]?.id || currentUser?.unit?.roles[0]?.id),
    }));

    const staticRoleOptions = staticRoles.map((role) => ({
      value: role,
      label: capitalize(role),
    }));

    return [...basicRoleOptions, ...staticRoleOptions];
  };

  const handleChangeRole = (value) => {
    const isUserRole = !(value === USER_ROLE ? '' : value);
    onCheckPermission(id, value, isUserRole);
  };

  const currentRole = () => {
    if (unitRoles) {
      return rowRoles[0]?.id ?? unitRoles[0]?.id ?? USER_ROLE;
    }
    return rowRoles[0]?.id ?? USER_ROLE;
  };

  return (
    <div className={`permission-select permission-select_row`}>
      <FormLabel>{label}</FormLabel>
      <FormGroup>
        <Select
          options={renderOptions()}
          value={currentRole()}
          handleChange={(value) => handleChangeRole(value)}
          variant="standard"
          disabled={
            !canUpdateRole ||
            id === currentUser.id ||
            rowRoles[0]?.id === (currentUser?.roles[0]?.id || currentUser?.unit?.roles[0]?.id)
          }
        />
      </FormGroup>
    </div>
  );
};

export default React.memo(PermissionsSelect);
